import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { ProductsService } from 'src/app/services/products.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { TireBrandCategory, TireBrandsResponse } from 'src/app/core/interfaces/products-types/tire-product.interface';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-tire-brand-dropdown',
  templateUrl: './tire-brand-dropdown.component.html',
  styleUrls: ['./tire-brand-dropdown.component.scss']
})


export class TireBrandDropdownComponent implements OnDestroy {

  showBrandDropdown = false;
  brandSearch: string = '';
  loading: boolean = false;
  maxVisibleChips: number = 5;

  OriginalMap: Map<TireBrandCategory, string[]> = new Map();
  filteredBrandMap: Map<TireBrandCategory, string[]> = new Map();

  @Input() multiSelect: boolean = false;
  @Input() brandFilter: string = "";
  @Input() brandFilters: string[] = [];
  @Output() brandFilterEmitter = new EventEmitter<string>();
  @Output() brandFiltersEmitter = new EventEmitter<string[]>();

  private brandFiltersChanged$ = new Subject<string[]>();
  private debounceSubscription: Subscription | null = null;

  constructor(
    private readonly eRef: ElementRef,
    private readonly productsService: ProductsService,
    private readonly translate: TranslateService
  ) {
    this.setOriginalData();
    this.setupDebounce();
  }

  ngOnDestroy(): void {
    this.debounceSubscription?.unsubscribe();
    this.brandFiltersChanged$.complete();
  }

  private setupDebounce(): void {
    this.debounceSubscription = this.brandFiltersChanged$
      .pipe(debounceTime(1000))
      .subscribe(filters => {
        this.brandFiltersEmitter.emit(filters);
      });
  }

  setOriginalData() {
    this.loading = true;
    this.productsService.getProductBrands()
      .pipe(
        finalize(() => this.loading = false)
      )
      .subscribe({
        next: (data: TireBrandsResponse) => {
          this.OriginalMap = this.getBrandMap(data);
          this.filteredBrandMap = new Map(this.OriginalMap);
        },
        error: (err) => {
          console.error(err);
        }
      });
  }


  getBrandMap(data: TireBrandsResponse): Map<TireBrandCategory, string[]> {
    const map = new Map<TireBrandCategory, string[]>();

    (Object.keys(data.brands) as TireBrandCategory[])
      .forEach(category => {
        map.set(category, data.brands[category]);
      });

    return map;
  }


  toggleBrandDropdown() {
    this.showBrandDropdown = !this.showBrandDropdown;
    if (this.showBrandDropdown) {
      this.brandSearch = '';
      this.filteredBrandMap = new Map(this.OriginalMap);
    }
  }

  getButtonLabel(): string {
    if (this.multiSelect) {
      if (this.brandFilters.length === 0) {
        return 'allBrands';
      }
      return '';
    }
    return this.brandFilter === "" ? 'allBrands' : '';
  }

  getSelectedCount(): number {
    return this.brandFilters.length;
  }

  isBrandSelected(brand: string): boolean {
    return this.brandFilters.includes(brand);
  }

  toggleBrand(brand: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const index = this.brandFilters.indexOf(brand);
    if (index > -1) {
      this.brandFilters = this.brandFilters.filter(b => b !== brand);
    } else {
      this.brandFilters = [...this.brandFilters, brand];
    }
    this.brandFiltersChanged$.next(this.brandFilters);
  }

  selectAllBrands(): void {
    const allBrands: string[] = [];
    this.OriginalMap.forEach(brands => {
      allBrands.push(...brands);
    });
    this.brandFilters = [...new Set(allBrands)];
    this.brandFiltersChanged$.next(this.brandFilters);
  }

  clearBrandSelection(): void {
    this.brandFilters = [];
    this.brandFiltersChanged$.next(this.brandFilters);
  }

  removeBrand(brand: string, event: Event): void {
    event.stopPropagation();
    this.brandFilters = this.brandFilters.filter(b => b !== brand);
    this.brandFiltersChanged$.next(this.brandFilters);
  }


  filterTyreBrandMap(
    map: Map<TireBrandCategory, string[]>,
    searchText: string
  ): Map<TireBrandCategory, string[]> {
    if (!searchText.trim()) return map;

    const lower = searchText.toLowerCase();

    const filteredEntries = Array.from(map.entries())
      .map(([category, brands]) => [
        category,
        brands.filter(b => b.toLowerCase().includes(lower))
      ] as [TireBrandCategory, string[]])
      .filter(([_, brands]) => brands.length > 0);

    return new Map(filteredEntries);
  }

  setBrandFilter(brand: string) {
    this.brandFilter = brand;
    this.showBrandDropdown = false;
    this.brandFilterEmitter.emit(this.brandFilter)
  }

  onBrandSearchChange() {
    this.filteredBrandMap = this.filterTyreBrandMap(new Map(this.OriginalMap), this.brandSearch)
  }

  getCategoryLabel(category: TireBrandCategory): string {
    const categoryString = String(category ?? '').trim();
    if (!categoryString) {
      return '';
    }

    const normalizedWithoutAccents = categoryString
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const candidates = [
      categoryString,
      normalizedWithoutAccents,
      normalizedWithoutAccents.replace(/\s+/g, ''),
      normalizedWithoutAccents.replace(/\s+/g, '_')
    ];

    for (const candidate of candidates) {
      const translationKey = `tyreBrandDropdown.categories.${candidate}`;
      const translated = this.translate.instant(translationKey);
      if (translated !== translationKey) {
        return translated;
      }
    }

    return categoryString;
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.showBrandDropdown = false;
    }
  }
}
